import cx from "clsx";
import SizingContainer from "common-utils/components/SizingContainer";
import { DisplayableError } from "kreate-common/modules/displayable-error";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { ArtworkResult } from "kreate-server/commands/GetArtworkList/typing";
import { httpPost$LoveArtwork } from "kreate-server/commands/LoveArtwork/fetcher";
import Link from "next/link";
import React from "react";
import { MdChat, MdFavorite, MdFavoriteBorder } from "react-icons/md";

import { getAuctionOverview } from "../../../../containers/PageArtwork/containers/ArtworkDetailsViewer/containers/PanelPrimaryAcquisition/containers/MethodAuctionEnglish/utils";
import SecondaryFileViewer from "../SecondaryFileViewer";
import UserDisplayNameViewerV2 from "../UserDisplayNameViewerV2";

import styles from "./index.module.scss";
import { getArtworkInventory } from "./utils";

import { useArtworkWindowApi } from "@/modules/artwork-window/hooks/useArtworkWindowApi";
import { useIsExpired } from "@/modules/common-hooks/useIsExpired";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import {
  formatInteger$Compact,
  formatInteger$Standard,
} from "@/modules/common-utils/format";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { AssetViewer$Ada$Compact } from "@/modules/kreate-components/components/AssetViewer$Ada$Compact";
import WithArtworkCensorship from "@/modules/kreate-components/components/WithArtworkCensorship";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import {
  getLinkToPageArtwork,
  getLinkToPageCollection,
} from "@/modules/navigation/utils/getLinkTo";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  artwork: ArtworkResult;
  sizes: string | undefined;
  openInNewTab?: boolean;
  /**
   * If `mustShowCollection` is enabled, the card artwork will display the label "Unsorted Collection"
   * in case the artworks does not belong to the collection. Otherwise, the card artwork will display the artist
   * name instead.
   */
  mustShowCollection: boolean;
};

export default function CardArtwork({
  className,
  style,
  artwork,
  sizes,
  openInNewTab,
  mustShowCollection,
}: Props) {
  const loginStatus = useLoginStatus();
  const { showMessage } = useToast();
  const isLoved =
    loginStatus?.loggedIn && artwork?.lovedBy?.includes(loginStatus.userId);
  const [pendingLoveState, setPendingLoveState] = React.useState<
    boolean | null
  >(null);
  const artworkInventory = getArtworkInventory(artwork);
  const artworkWindowApi = useArtworkWindowApi();
  const auctionOverview = getAuctionOverview(artwork);
  const isAuctionStarted = useIsExpired(auctionOverview?.startedAt);
  const isAuctionEnded = useIsExpired(auctionOverview?.endedAt);

  const collectionName = artwork.populated?.collection
    ? artwork.populated?.collection?.title || EM_DASH
    : null;

  const customUnsortedCollectionName =
    artwork.populated?.createdBy &&
    artwork.populated.createdBy.customUnsortedCollectionName
      ? artwork.populated.createdBy.customUnsortedCollectionName
      : null;

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col gap="20px">
        <WithArtworkCensorship
          className={styles.imageContainer}
          isNSFW={artwork?.isNSFW || false}
          flagPhotosensitivity={artwork?.flagPhotosensitivity || false}
        >
          <Link
            href={getLinkToPageArtwork(artwork)}
            onClick={(event) => {
              artworkWindowApi.visitArtwork(artwork.artworkId, {
                openInNewTab,
              });
              event.preventDefault();
            }}
          >
            <SizingContainer
              sizing={{ type: "auto-height", aspectRatio: artwork.file }}
            >
              <SecondaryFileViewer
                file={artwork.file}
                thumbnailFile={artwork.imageFile}
                sizes={sizes}
                isSingleEdition={artwork.totalSupply === 1}
                fill
              />
            </SizingContainer>
          </Link>
        </WithArtworkCensorship>
        <Flex.Col gap="8px">
          <Flex.Row justifyContent="space-between" gap="12px">
            <Link
              href={getLinkToPageArtwork(artwork)}
              onClick={(event) => {
                artworkWindowApi.visitArtwork(artwork.artworkId, {
                  openInNewTab,
                });
                event.preventDefault();
              }}
            >
              <div className={styles.title}>{artwork.title || EM_DASH}</div>
            </Link>
            <Flex.Row alignItems="center" gap="12px">
              <button
                className={cx(
                  styles.loveButton,
                  isLoved ? styles.isLoved : null
                )}
                disabled={pendingLoveState !== null}
                onClick={async () => {
                  if (loginStatus == null) {
                    return;
                  }
                  if (!loginStatus?.loggedIn) {
                    showMessage({
                      color: "warning",
                      title: "You have to login to perform this action.",
                    });
                    return;
                  }
                  setPendingLoveState(!isLoved);
                  try {
                    await httpPost$LoveArtwork(
                      `/api/v1/artwork/${artwork?.artworkId}/love`,
                      {
                        love: !isLoved,
                      }
                    );
                    await shamelesslyRevalidateEverything();
                  } catch (error) {
                    const displayableError = DisplayableError.from(error);
                    showMessage({
                      color: "danger",
                      title: displayableError.title,
                      description: displayableError.description,
                    });
                  } finally {
                    setPendingLoveState(null);
                  }
                }}
              >
                {!isLoved ? (
                  <MdFavoriteBorder size="20px" />
                ) : (
                  <MdFavorite size="20px" />
                )}
                <div className={!isLoved ? styles.colorFgMain : undefined}>
                  {artwork.lovedBy.length}
                </div>
              </button>
              <Flex.Row gap="4px" alignItems="center">
                <MdChat size="20px" className={styles.colorFgDimmed} />
                <div className={styles.stats}>
                  {artwork.totalNumComments ?? EM_DASH}
                </div>
              </Flex.Row>
            </Flex.Row>
          </Flex.Row>

          <Flex.Row justifyContent="space-between" gap="12px">
            {mustShowCollection || artwork.collectionId ? (
              <Flex.Cell className={styles.textCollection}>
                <span style={{ fontStyle: "italic" }}>{"Collection: "}</span>
                <Link
                  href={
                    artwork.populated?.collection
                      ? getLinkToPageCollection(artwork.populated?.collection)
                      : `/user/${artwork.createdBy}`
                  }
                  target={openInNewTab ? "_blank" : undefined}
                  rel={openInNewTab ? "noreferrer" : undefined}
                >
                  {artwork.populated?.collection
                    ? collectionName
                    : customUnsortedCollectionName || "Unsorted Collection"}
                </Link>
              </Flex.Cell>
            ) : (
              <Flex.Row
                className={styles.textArtist}
                alignItems="center"
                gap="4px"
                minWidth={0}
              >
                <span style={{ fontStyle: "italic" }}>{"by "}</span>
                <Flex.Cell flex="0 1 auto" minWidth={0}>
                  <UserDisplayNameViewerV2
                    user={artwork.populated?.createdBy}
                    unstyled
                    openInNewTab={openInNewTab}
                  />
                </Flex.Cell>
              </Flex.Row>
            )}
            {artwork.populated?.createdBy
              .flagSuspended ? null : auctionOverview ? (
              <Flex.Row
                className={styles.stats}
                gap="12px"
                flex="0 0 auto"
                alignItems="center"
              >
                {isAuctionEnded
                  ? "Auction Ended"
                  : isAuctionStarted
                  ? "Auction Ongoing"
                  : "Auction Soon"}
              </Flex.Row>
            ) : artworkInventory ? (
              <Flex.Row
                className={styles.stats}
                gap="12px"
                flex="0 0 auto"
                alignItems="center"
              >
                <Flex.Cell>
                  {artworkInventory.available !== 0 ? (
                    <>
                      <span
                        className={styles.colorFgMain}
                        title={`${formatInteger$Standard(
                          artworkInventory.available
                        )}/${formatInteger$Standard(artwork.totalSupply)}`}
                      >{`${formatInteger$Compact(
                        artworkInventory.available
                      )}/${formatInteger$Compact(artwork.totalSupply)}`}</span>
                      <span className={styles.colorFgDimmed}>
                        {" Available"}
                      </span>
                    </>
                  ) : (
                    <span className={styles.colorFgDimmed}>{"Sold Out"}</span>
                  )}
                </Flex.Cell>
                <Divider.Vertical />
                <AssetViewer$Ada$Compact
                  as={"span"}
                  lovelaceAmount={artworkInventory.price}
                  className={styles.colorFgMain}
                />
              </Flex.Row>
            ) : null}
          </Flex.Row>
        </Flex.Col>
      </Flex.Col>
    </div>
  );
}
